import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const StagePopup = ({
  registrationsData,
  workflowStatus,
  nextSteps,
  onClose,
}) => {
  // Hardcoded stages data for now, including inprocess and overdue
  const stages = [
    { stage: "Application Review Pending", inprocess: 14, overdue: 7 },
    { stage: "Accounts Approval Pending", inprocess: 4, overdue: 2 },
    { stage: "Admission Payment Proof Pending", inprocess: 8, overdue: 3 },
    { stage: "Registration Payment Proof Pending", inprocess: 2, overdue: 1 },
    { stage: "Challan Verification Pending", inprocess: 7, overdue: 1 },
    { stage: "School Assessment Pending", inprocess: 10, overdue: 5 },
    { stage: "Student Creation Pending", inprocess: 9, overdue: 4 },
    { stage: "Interview Remarks Pending", inprocess: 4, overdue: 2 },
  ];

  if (registrationsData.length === 0) {
    return (
      <div
        className="modal fade show d-block"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title" style={{ color: "white" }}>
                Student Stages Overview
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <p>No data available for stages.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Chart.js data for Horizontal Stacked Bar Chart
  const barChartData = {
    labels: stages.map((entry) => entry.stage),
    datasets: [
      {
        label: "Inprocess Applications",
        data: stages.map((entry) => entry.inprocess),
        backgroundColor: "#0088FE", // Blue
        borderColor: "#0088FE",
        borderWidth: 1,
      },
      {
        label: "Overdue Applications",
        data: stages.map((entry) => entry.overdue),
        backgroundColor: "#FF8042", // Orange
        borderColor: "#FF8042",
        borderWidth: 1,
      },
    ],
  };

  // Bar Chart options for horizontal stacked with data labels
  const barChartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        enabled: true,
        bodyFont: {
          size: 12,
        },
      },
      // Custom plugin to display values inside bars
      afterDraw: (chart) => {
        const ctx = chart.ctx;
        chart.data.datasets.forEach((dataset, i) => {
          const meta = chart.getDatasetMeta(i);
          meta.data.forEach((bar, index) => {
            const data = dataset.data[index];
            if (data > 0) {
              // Only display text if value is greater than 0
              ctx.fillStyle = "#fff";
              ctx.font = "bold 11px Arial";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              const xPos = bar.x - bar.width / 2 + 15;
              const yPos = bar.y;
              ctx.fillText(data, xPos, yPos);
            }
          });
        });
      },
    },
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      y: {
        stacked: true,
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        style={{ maxWidth: "85vw" }}
      >
        <div
          className="modal-content"
          style={{
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
          }}
        >
          <div className="modal-header bg-primary text-white py-2">
            <h5
              className="modal-title m-0"
              style={{ color: "white", fontSize: "18px" }}
            >
              Student Stages Overview
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body py-2">
            <p
              className="mb-3 text-center fw-bold p-2 rounded"
              style={{
                fontSize: "15px",
                backgroundColor: "#e8f4ff",
                border: "1px solid #b8daff",
                color: "#004085",
              }}
            >
              Total Students: {registrationsData.length}
            </p>
            <div className="row">
              {/* Table */}
              <div className="col-md-6">
                <h6
                  className="text-center mb-2 p-1"
                  style={{
                    backgroundColor: "#f0f8ff",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  New Registrations Status
                </h6>
                <div style={{ height: "280px" }}>
                  <table
                    className="table table-bordered"
                    style={{ fontSize: "12px", marginBottom: "0" }}
                  >
                    <thead>
                      <tr style={{ height: "28px" }}>
                        <th
                          style={{
                            fontWeight: "bold",
                            padding: "4px",
                            fontSize: "12px",
                            backgroundColor: "#f8f9fa",
                          }}
                        >
                          Status
                        </th>
                        <th
                          style={{
                            fontWeight: "bold",
                            padding: "4px",
                            fontSize: "12px",
                            backgroundColor: "#f8f9fa",
                          }}
                        >
                          Inprocess
                        </th>
                        <th
                          style={{
                            fontWeight: "bold",
                            padding: "4px",
                            fontSize: "12px",
                            backgroundColor: "#f8f9fa",
                          }}
                        >
                          Overdue
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {stages.map((entry, index) => (
                        <tr key={index} style={{ height: "32px" }}>
                          <td style={{ padding: "4px", fontSize: "11px" }}>
                            {entry.stage}
                          </td>
                          <td style={{ padding: "4px", fontSize: "11px" }}>
                            {entry.inprocess}
                          </td>
                          <td
                            style={{
                              padding: "4px",
                              color: "red",
                              fontSize: "11px",
                            }}
                          >
                            {entry.overdue}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Horizontal Stacked Bar Chart */}
              <div className="col-md-6">
                <h6
                  className="text-center mb-2 p-1"
                  style={{
                    backgroundColor: "#f0f8ff",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Students per Stage (Bar Chart)
                </h6>
                <div style={{ height: "280px" }}>
                  <Bar data={barChartData} options={barChartOptions} />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer py-2">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StagePopup;
